import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import type { KeyValuePair } from '@/utils/keyValueJson';

export type KeyValueFieldOption = { value: string; label: string; placeholder?: string };

type Props = {
  label?: string;
  hint?: string;
  pairs: KeyValuePair[];
  onChange: (pairs: KeyValuePair[]) => void;
  /** When set, field column uses a dropdown of known fields. */
  fieldOptions?: KeyValueFieldOption[];
  valuePlaceholder?: string;
  addLabel?: string;
  allowCustomField?: boolean;
};

function isKnownField(key: string, fieldOptions?: KeyValueFieldOption[]) {
  return Boolean(fieldOptions?.some((o) => o.value === key));
}

export function KeyValueFieldsEditor({
  label,
  hint,
  pairs,
  onChange,
  fieldOptions,
  valuePlaceholder = 'Value',
  addLabel = 'Add field',
  allowCustomField = true,
}: Props) {
  const updatePair = (index: number, patch: Partial<KeyValuePair>) => {
    onChange(pairs.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  };

  const removePair = (index: number) => {
    const next = pairs.filter((_, i) => i !== index);
    onChange(next.length > 0 ? next : [{ key: '', value: '' }]);
  };

  const addPair = () => {
    onChange([...pairs, { key: '', value: '' }]);
  };

  return (
    <div className="space-y-2">
      {label ? <p className="label">{label}</p> : null}
      {hint ? <p className="text-xs text-slate-500 dark:text-slate-400">{hint}</p> : null}
      <div className="space-y-2">
        {pairs.map((pair, index) => {
          const known = isKnownField(pair.key, fieldOptions);
          const pickCustom = Boolean(fieldOptions && allowCustomField && pair.key !== '' && !known);
          const selectValue = pair.key === '' ? '' : known ? pair.key : '__custom__';
          const selectedOption = fieldOptions?.find((o) => o.value === pair.key);

          return (
            <div key={index} className="space-y-2 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
              <div className="grid gap-2 sm:grid-cols-2">
                {fieldOptions ? (
                  <Select
                    label="Field"
                    value={selectValue}
                    placeholder="Select field"
                    options={[
                      ...fieldOptions.map((o) => ({ value: o.value, label: o.label })),
                      ...(allowCustomField ? [{ value: '__custom__', label: 'Custom field…' }] : []),
                    ]}
                    onChange={(v) => {
                      if (v === '__custom__') updatePair(index, { key: '' });
                      else updatePair(index, { key: v });
                    }}
                  />
                ) : (
                  <Input
                    label="Field name"
                    placeholder="e.g. country"
                    value={pair.key}
                    onChange={(e) => updatePair(index, { key: e.target.value })}
                  />
                )}
                <Input
                  label="Value"
                  placeholder={selectedOption?.placeholder ?? valuePlaceholder}
                  value={pair.value}
                  onChange={(e) => updatePair(index, { value: e.target.value })}
                />
              </div>
              {pickCustom ? (
                <Input
                  label="Custom field name"
                  placeholder="e.g. productCode"
                  value={pair.key}
                  onChange={(e) => updatePair(index, { key: e.target.value })}
                />
              ) : null}
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removePair(index)}
                >
                  <DeleteOutlineIcon fontSize="small" /> Remove
                </Button>
              </div>
            </div>
          );
        })}
      </div>
      <Button type="button" variant="secondary" size="sm" onClick={addPair}>
        <AddIcon fontSize="small" /> {addLabel}
      </Button>
    </div>
  );
}
